// Colour as theme tokens hold it: a whole CSS colour, oklch(L C H) (lightness 0 to 1, chroma, hue in degrees), read into
// sRGB and written back; the colour, amount or length of any token as a set holds it, or as its formula makes it for a
// new set (fill.ts); and contrast between two colours.
import { tokenSpec, type Derive, type MixAmount } from './tokens';

export type Rgb = [number, number, number];

/** A colour in sRGB, 0 to 255 a channel, and its opacity, 0 to 1. */
export type TokenColour = { rgb: Rgb; alpha: number };

export const hexToRgb = (hex: string): Rgb => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

export const rgbToHex = ([r, g, b]: Rgb) =>
  `#${[r, g, b].map(c => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('')}`;

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number) => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055);

type Lab = [number, number, number];

function rgbToOklab([r, g, b]: Rgb): Lab {
  const [lr, lg, lb] = [r, g, b].map(c => toLinear(c / 255));
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
}

function oklabToRgb([L, a, b]: Lab): Rgb {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const linear = [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s];
  return linear.map(c => Math.min(255, Math.max(0, toGamma(c) * 255))) as Rgb;
}

const oklchToRgb = (l: number, c: number, h: number): Rgb => oklabToRgb([l, c * Math.cos((h * Math.PI) / 180), c * Math.sin((h * Math.PI) / 180)]);

const trim = (n: number, places: number) => String(Number(n.toFixed(places)));

/** An OKLCH colour, lightness 0 to 1, chroma and hue in degrees. */
export type Oklch = { l: number; c: number; h: number };

// Whether an OKLCH colour is inside sRGB, unclamped
function inGamut({ l, c, h }: Oklch): boolean {
  const a = c * Math.cos((h * Math.PI) / 180);
  const b = c * Math.sin((h * Math.PI) / 180);
  const L = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const M = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const S = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const rgb = [4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S, -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S, -0.0041960863 * L - 0.7034186147 * M + 1.707614701 * S];
  return rgb.every(v => v >= -0.0005 && v <= 1.0005);
}

/** The colour with its chroma brought into sRGB at the same lightness and hue, rather than clipped (which shifts hue). */
export function fitGamut(colour: Oklch): Oklch {
  const l = Math.min(1, Math.max(0, colour.l));
  if (inGamut({ ...colour, l })) return { ...colour, l };
  let lo = 0;
  let hi = colour.c;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    if (inGamut({ l, c: mid, h: colour.h })) lo = mid;
    else hi = mid;
  }
  return { l, c: lo, h: colour.h };
}

/** An OKLCH colour in sRGB, its chroma fitted to the gamut. */
export const oklchToSrgb = (colour: Oklch): Rgb => {
  const { l, c, h } = fitGamut(colour);
  return oklchToRgb(l, c, h);
};

/** sRGB as OKLCH. */
export function srgbToOklch(rgb: Rgb): Oklch {
  const [l, a, b] = rgbToOklab(rgb);
  const c = Math.hypot(a, b);
  return { l, c, h: c < 0.0004 ? 0 : ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360 };
}

/** An OKLCH colour as a set stores it, oklch(L C H), fitted to the gamut. */
export function formatOklch(colour: Oklch): string {
  const { l, c, h } = fitGamut(colour);
  return c < 0.0004 ? `oklch(${trim(l, 4)} 0 0)` : `oklch(${trim(l, 4)} ${trim(c, 4)} ${trim(((h % 360) + 360) % 360, 2)})`;
}

/**
 * The lightness at which a colour of this hue and chroma reaches `minimum` contrast against `against`, as close to it
 * as that allows: darker against a light colour, lighter against a dark one. The extreme where it never reaches it.
 */
export function solveLightness(hue: number, chroma: number, against: Rgb, minimum: number): number {
  const darker = luminance(against) > 0.18;
  const reaches = (l: number) => contrast(oklchToSrgb({ l, c: chroma, h: hue }), against) >= minimum;
  if (!reaches(darker ? 0 : 1)) return darker ? 0 : 1;
  let near = srgbToOklch(against).l;
  let far = darker ? 0 : 1;
  for (let i = 0; i < 24; i++) {
    const mid = (near + far) / 2;
    if (reaches(mid)) far = mid;
    else near = mid;
  }
  return far;
}

/** sRGB as OKLCH channels, `L C H`, what writeColour puts inside oklch(). */
export function oklchChannels(rgb: Rgb): string {
  const [l, a, b] = rgbToOklab(rgb);
  const c = Math.hypot(a, b);
  if (c < 0.0004) return `${trim(l, 4)} 0 0`;
  return `${trim(l, 4)} ${trim(c, 4)} ${trim(((Math.atan2(b, a) * 180) / Math.PI + 360) % 360, 2)}`;
}

/** Two colours mixed in OKLab, `amount` of the way from `a` to `b`, as color-mix(in oklab) does. */
export function mixColours(a: TokenColour, b: TokenColour, amount: number): TokenColour {
  const [la, lb] = [rgbToOklab(a.rgb), rgbToOklab(b.rgb)];
  return { rgb: oklabToRgb(la.map((v, i) => v + (lb[i] - v) * amount) as Lab), alpha: a.alpha + (b.alpha - a.alpha) * amount };
}

const NUMBER = String.raw`-?(?:\d+\.?\d*|\.\d+)`;
const CHANNELS = new RegExp(`^(${NUMBER})\\s+(${NUMBER})\\s+(${NUMBER})(?:\\s*/\\s*(${NUMBER}))?$`);
const OKLCH = new RegExp(`^oklch\\(\\s*(${NUMBER})\\s+(${NUMBER})\\s+(${NUMBER})(?:\\s*/\\s*(${NUMBER}))?\\s*\\)$`);
const RGBA = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/;
const HEX = /^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** A colour from OKLCH channels, oklch(), hex (#f00 and #ff0000, with or without opacity) or rgb()/rgba(); null for anything else (a gradient, var()). */
export function readColour(value: string): TokenColour | null {
  const text = value.trim();
  if (text === 'transparent') return { rgb: [0, 0, 0], alpha: 0 };
  const lch = text.match(CHANNELS) ?? text.match(OKLCH);
  if (lch) return { rgb: oklchToRgb(Number(lch[1]), Number(lch[2]), Number(lch[3])), alpha: lch[4] === undefined ? 1 : Number(lch[4]) };
  const rgba = text.match(RGBA);
  if (rgba) return { rgb: [Number(rgba[1]), Number(rgba[2]), Number(rgba[3])], alpha: rgba[4] === undefined ? 1 : Number(rgba[4]) };
  const digits = text.match(HEX)?.[1];
  // #f00 is #ff0000, each digit doubled
  const hex = digits && digits.length <= 4 ? [...digits].map(digit => digit + digit).join('') : digits;
  if (hex) return { rgb: hexToRgb(hex.slice(0, 6)), alpha: hex.length === 8 ? parseInt(hex.slice(6), 16) / 255 : 1 };
  return null;
}

/** A colour as a set stores it: a whole CSS colour, oklch(L C H), with its opacity where it has one. */
export const writeColour = ({ rgb, alpha }: TokenColour): string => `oklch(${oklchChannels(rgb)}${alpha < 1 ? ` / ${trim(alpha, 3)}` : ''})`;

/** A theme's values as tokenColour and tokenAmount read them: its tokens, and `dark` from its mode. */
export const themeValues =
  (tokens: Record<string, string>, mode: 'light' | 'dark') =>
  (name: string): string | undefined =>
    name === 'lureo-dark' ? (mode === 'dark' ? '1' : '0') : tokens[name];

// How far a mix goes in the theme `valueOf` gives: fixed, between its light and dark amounts, or an amount token's
function mixAmount(amount: MixAmount, valueOf: (name: string) => string | undefined, depth: number): number {
  if (typeof amount === 'number') return amount;
  if (typeof amount === 'string') return tokenAmount(amount, valueOf, depth + 1);
  const dark = tokenAmount('lureo-dark', valueOf, depth + 1);
  return amount[0] + (amount[1] - amount[0]) * dark;
}

// A colour from its formula
function deriveColour(derive: Derive, valueOf: (name: string) => string | undefined, depth: number): TokenColour | null {
  if ('alias' in derive) return tokenColour(derive.alias, valueOf, depth + 1);
  if ('mix' in derive) {
    const [from, to, amount] = derive.mix;
    const a = tokenColour(from, valueOf, depth + 1);
    const b = tokenColour(to, valueOf, depth + 1);
    return a && b ? mixColours(a, b, mixAmount(amount, valueOf, depth)) : null;
  }
  if ('alpha' in derive) {
    const [colour, opacity, scaledBy, offset = 0] = derive.alpha;
    const base = tokenColour(colour, valueOf, depth + 1);
    const scale = scaledBy ? tokenAmount(scaledBy, valueOf, depth + 1) : 1;
    const alpha = scaledBy ? offset + opacity * (Number.isFinite(scale) ? scale : 1) : opacity;
    return base && { rgb: base.rgb, alpha: base.alpha * alpha };
  }
  if ('shift' in derive) {
    const [colour, by, scaledBy, flips] = derive.shift;
    const base = tokenColour(colour, valueOf, depth + 1);
    if (!base) return null;
    const scale = (scaledBy ? tokenAmount(scaledBy, valueOf, depth + 1) || 0 : 1) * (flips ? 1 - 2 * tokenAmount('lureo-dark', valueOf, depth + 1) : 1);
    const { l, c, h } = srgbToOklch(base.rgb);
    const moved = { l: l + (by.l ?? 0) * scale, c: Math.max(0, c + (by.c ?? 0) * scale), h: h + (by.h ?? 0) * scale };
    return { rgb: oklchToSrgb(moved), alpha: base.alpha };
  }
  if ('fixed' in derive) return readColour(derive.fixed);
  if ('modes' in derive) {
    const light = deriveColour(derive.modes[0], valueOf, depth + 1);
    const dark = deriveColour(derive.modes[1], valueOf, depth + 1);
    return light && dark ? mixColours(light, dark, tokenAmount('lureo-dark', valueOf, depth + 1)) : null;
  }
  return null;
}

/**
 * The colour of any token from `valueOf`, which gives a token's value as a set holds it (themeValues gives a set's, with
 * its darkness): the value, where it is a colour, else its formula's colour (for fill.ts, which hands it formulas). Null
 * for a token that is not a colour or cannot be read.
 */
export function tokenColour(name: string, valueOf: (name: string) => string | undefined, depth = 0): TokenColour | null {
  if (depth > 12) return null;
  const value = valueOf(name)?.trim();
  const own = value ? readColour(value) : null;
  if (own) return own;
  const derive = tokenSpec(name)?.derive;
  return derive ? deriveColour(derive, valueOf, depth) : null;
}

// A plain number, in pixels for a length
function ownNumber(name: string, valueOf: (name: string) => string | undefined): number {
  const value = valueOf(name)?.trim() ?? '';
  const m = value.match(/^(-?\d*\.?\d+)(px|rem)?$/);
  return m ? Number(m[1]) * (m[2] === 'rem' ? 16 : 1) : NaN;
}

/** An amount token's value as a set holds it, else its fixed value; Dark is 0 in a set without a mode, which reads as light. */
export function tokenAmount(name: string, valueOf: (name: string) => string | undefined, depth = 0): number {
  const own = ownNumber(name, valueOf);
  if (Number.isFinite(own) || depth >= 12) return own;
  const derive = tokenSpec(name)?.derive;
  if (derive) {
    if ('fixed' in derive) return Number(derive.fixed);
    if ('mode' in derive) return 0;
  }
  return NaN;
}

/**
 * A number token's value (an amount, a weight, a layer, or a length in pixels at 16px a rem) as a set holds it, else as
 * its formula makes it, calc(), min() and max() over other tokens included; NaN where it is none (a shadow, a gradient).
 */
export function tokenNumber(name: string, valueOf: (name: string) => string | undefined, depth = 0): number {
  const own = ownNumber(name, valueOf);
  if (Number.isFinite(own) || depth >= 12) return own;
  const derive = tokenSpec(name)?.derive;
  if (!derive) return NaN;
  if ('fixed' in derive) return ownNumber(name, () => derive.fixed);
  if ('mode' in derive) return tokenAmount(name, valueOf, depth);
  if ('alias' in derive) return tokenNumber(derive.alias, valueOf, depth + 1);
  if (!('css' in derive)) return NaN;
  // calc(var(--lureo-radius) * 2 / 3), max(100, calc(var(--lureo-weight-heading) - 100)), in pixels
  const expression = derive.css
    .replace(/var\(--([\w-]+)\)/g, (_m, source: string) => `(${tokenNumber(source, valueOf, depth + 1)})`)
    .replace(/(\d*\.?\d+)px/g, '$1')
    .replace(/calc\(/g, '(')
    .replace(/(min|max)\(/g, 'Math.$1(');
  if (!/^[\d\s.+\-*/(),]*$/.test(expression.replace(/Math\.(min|max)/g, ''))) return NaN;
  try {
    return Number(new Function(`return ${expression}`)());
  } catch {
    return NaN;
  }
}

const luminance = (rgb: Rgb) => {
  const [r, g, b] = rgb.map(c => toLinear(c / 255));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two colours, 1 to 21. */
export function contrast(a: Rgb, b: Rgb) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}
